import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, FileDown, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { Panel, Field, Money, NativeSelect, Loading } from "@/components/kit";
import { errMsg } from "@/lib/format";

const AdjList = ({ title, items, setItems, rw, kind }) => (
  <div className="space-y-2" data-testid={`tax-${kind}`}>
    <div className="eyebrow">{title}</div>
    {items.map((x, i) => (
      <div key={i} className="flex gap-2">
        <Input value={x.label} disabled={!rw} onChange={(e) => setItems(items.map((y, j) => (j === i ? { ...y, label: e.target.value } : y)))} data-testid={`tax-${kind}-label-${i}`} />
        <Input type="number" step="0.001" className="w-36" value={x.amount} disabled={!rw} onChange={(e) => setItems(items.map((y, j) => (j === i ? { ...y, amount: e.target.value } : y)))} data-testid={`tax-${kind}-amount-${i}`} />
        {rw && <Button size="icon" variant="ghost" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`tax-${kind}-remove-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
      </div>
    ))}
    {rw && <Button size="sm" variant="outline" onClick={() => setItems([...items, { label: "", amount: "" }])} data-testid={`tax-${kind}-add`}><Plus className="h-3.5 w-3.5 me-1" />{title}</Button>}
  </div>
);

const Row = ({ k, v, bold, testid }) => <div className={`flex justify-between border-b border-slate-50 py-1.5 text-sm ${bold ? "font-semibold text-slate-900" : "text-slate-600"}`} data-testid={testid}><span>{k}</span><Money v={v} /></div>;

export const TaxPanel = ({ year }) => {
  const { api, t, can, download } = useApp();
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const rw = can("accounting", true);
  useEffect(() => { setD(null); api.get(`/closing/${year}/tax`).then((r) => { setD(r.data); setF(r.data.inputs); }); }, [api, year]);
  if (!d || !f) return <Panel title={t("liasse")}><Loading /></Panel>;
  const num = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const clean = (l) => l.filter((x) => x.label.trim()).map((x) => ({ label: x.label.trim(), amount: +x.amount || 0 }));
    try {
      const { data } = await api.put(`/closing/${year}/tax`, { ...f, rate: +f.rate, reintegrations: clean(f.reintegrations), deductions: clean(f.deductions),
        deficits_brought_forward: +f.deficits_brought_forward || 0, prepayments: +f.prepayments || 0, withholdings: +f.withholdings || 0 });
      setD(data); setF(data.inputs); toast.success(t("saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const c = d.calc;
  return (
    <Panel title={`${t("liasse")} ${year}`} testid="tax-panel">
      <div className="grid gap-8 lg:grid-cols-2">
        <div className="space-y-4">
          <AdjList title={t("tax_reint")} kind="reint" items={f.reintegrations} setItems={(v) => setF({ ...f, reintegrations: v })} rw={rw} />
          <AdjList title={t("tax_ded")} kind="ded" items={f.deductions} setItems={(v) => setF({ ...f, deductions: v })} rw={rw} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("tax_rate")}><NativeSelect value={f.rate} onChange={num("rate")} disabled={!rw} data-testid="tax-rate">{[10, 15, 20, 35].map((r) => <option key={r} value={r}>{r} %</option>)}</NativeSelect></Field>
            <Field label={t("tax_deficits")}><Input type="number" step="0.001" value={f.deficits_brought_forward} onChange={num("deficits_brought_forward")} disabled={!rw} data-testid="tax-deficits" /></Field>
            <Field label={t("tax_prepay")}><Input type="number" step="0.001" value={f.prepayments} onChange={num("prepayments")} disabled={!rw} data-testid="tax-prepayments" /></Field>
            <Field label={t("tax_rs")}><Input type="number" step="0.001" value={f.withholdings} onChange={num("withholdings")} disabled={!rw} data-testid="tax-withholdings" /></Field>
          </div>
          {rw && <Button onClick={save} data-testid="tax-save">{t("tax_compute")}</Button>}
        </div>
        <div>
          <Row k={t("tax_acc_result")} v={c.accounting_result_before_tax} />
          <Row k={`+ ${t("tax_reint")}`} v={c.reintegrations} /><Row k={`− ${t("tax_ded")}`} v={c.deductions} />
          <Row k={t("tax_fiscal_result")} v={c.fiscal_result} bold testid="tax-fiscal-result" />
          <Row k={t("tax_taxable")} v={c.taxable} /><Row k={`IS ${c.rate} %`} v={c.is_at_rate} />
          <Row k={`${t("tax_minimum")} (${c.minimum_rate} % CA, min. 500)`} v={c.minimum_tax} />
          <Row k={t("tax_due")} v={c.is_due} bold testid="tax-is-due" />
          {c.minimum_applies && <p className="text-xs text-orange-700" data-testid="tax-minimum-applies">{t("tax_min_applies")}</p>}
          <Row k={t("tax_credits")} v={c.credits} />
          <div className="mt-2 flex justify-between rounded-lg bg-slate-900 px-3 py-2 text-sm font-bold text-white" data-testid="tax-net-to-pay"><span>{t("tax_net")}</span><Money v={c.net_to_pay} /></div>
          {c.carry_credit > 0 && <Row k={t("tax_carry")} v={c.carry_credit} />}
          {c.new_deficit > 0 && <Row k={t("tax_new_deficit")} v={c.new_deficit} />}
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={() => download(`/closing/${year}/liasse.pdf`, `liasse-fiscale-${year}.pdf`)} data-testid="liasse-pdf"><FileDown className="h-4 w-4 me-1" />PDF</Button>
            <Button variant="outline" onClick={() => download(`/closing/${year}/liasse.xlsx`, `liasse-fiscale-${year}.xlsx`, false)} data-testid="liasse-xlsx"><FileSpreadsheet className="h-4 w-4 me-1" />Excel</Button>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">{t("liasse_note")}</p>
        </div>
      </div>
    </Panel>
  );
};
